import React, { useState, useMemo } from 'react';
import './QuestionnaireSatisfaction.css';

// Fonction pour générer un code aléatoire
const generateCaptchaCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Évite les caractères ambigus (0, O, I, 1)
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

const QuestionnaireSatisfaction = () => {
  // Générer un code aléatoire à chaque chargement du composant
  const captchaText = useMemo(() => generateCaptchaCode(), []);

  const [formData, setFormData] = useState({
    accueil: '',
    evaluation: '',
    horaires: '',
    documents: '',
    outilsTheorie: '',
    outilsPratique: '',
    enseignement: '',
    disponibilite: '',
    parcoursIntegral: '',
    coursThematiques: '',
    enseignantPresent: '',
    rythme: '',
    duree: '',
    noteGlobale: '5',
    commentaire: '',
    captcha: '',
    consentement: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');

  const satisfactionOptions = [
    { value: 'tres_satisfait', label: 'Tout à fait satisfait' },
    { value: 'satisfait', label: 'Satisfait' },
    { value: 'peu_satisfait', label: 'Peu satisfait' },
    { value: 'pas_satisfait', label: 'Pas du tout satisfait' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitStatus('');

    // Validation
    if (formData.captcha.toUpperCase() !== captchaText) {
      setSubmitMessage('Le code de vérification est incorrect.');
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await fetch('/api/send-questionnaire', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'questionnaire',
          responses: formData
        })
      });
      const data = await res.json();

      if (res.ok) {
        setSubmitMessage('Merci ! Votre questionnaire a été envoyé avec succès.');
        setSubmitStatus('success');
        // Reset form
        setFormData({
          accueil: '',
          evaluation: '',
          horaires: '',
          documents: '',
          outilsTheorie: '',
          outilsPratique: '',
          enseignement: '',
          disponibilite: '',
          parcoursIntegral: '',
          coursThematiques: '',
          enseignantPresent: '',
          rythme: '',
          duree: '',
          noteGlobale: '5',
          commentaire: '',
          captcha: ''
        });
      } else {
        setSubmitMessage(data.error || 'Erreur lors de l\'envoi. Réessayez ou contactez-nous au 09 52 65 50 19.');
        setSubmitStatus('error');
      }
    } catch (err) {
      setSubmitMessage('Erreur de connexion. Vérifiez votre réseau ou contactez-nous au 09 52 65 50 19.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="questionnaire-page">
      <section className="questionnaire-hero">
        <div className="container">
          <h1 className="questionnaire-title">Votre expérience compte</h1>
          <p className="questionnaire-subtitle">
            Partagez votre retour pour nous aider à progresser ensemble
          </p>
        </div>
      </section>

      <section className="questionnaire-content">
        <div className="container">
          <form className="questionnaire-form" onSubmit={handleSubmit}>
            
            {/* Accueil */}
            <div className="form-section">
              <h2 className="section-title">
                <span className="section-number">1</span>
                Premier contact
              </h2>
              <div className="question-group">
                <label className="question-label">Comment avez-vous vécu votre accueil ?</label>
                <div className="radio-group">
                  {satisfactionOptions.map(option => (
                    <label key={option.value} className="radio-option">
                      <input
                        type="radio"
                        name="accueil"
                        value={option.value}
                        checked={formData.accueil === option.value}
                        onChange={handleChange}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Information sur l'offre de formation */}
            <div className="form-section">
              <h2 className="section-title">
                <span className="section-number">2</span>
                Informations & organisation
              </h2>
              
              <div className="question-group">
                <label className="question-label">L'évaluation de départ était-elle claire et pertinente ?</label>
                <div className="radio-group">
                  {satisfactionOptions.map(option => (
                    <label key={option.value} className="radio-option">
                      <input
                        type="radio"
                        name="evaluation"
                        value={option.value}
                        checked={formData.evaluation === option.value}
                        onChange={handleChange}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="question-group">
                <label className="question-label">Les horaires des cours théoriques vous convenaient-ils ?</label>
                <div className="radio-group">
                  {satisfactionOptions.map(option => (
                    <label key={option.value} className="radio-option">
                      <input
                        type="radio"
                        name="horaires"
                        value={option.value}
                        checked={formData.horaires === option.value}
                        onChange={handleChange}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="question-group">
                <label className="question-label">Les documents et présentations étaient-ils clairs et compréhensibles ?</label>
                <div className="radio-group">
                  {satisfactionOptions.map(option => (
                    <label key={option.value} className="radio-option">
                      <input
                        type="radio"
                        name="documents"
                        value={option.value}
                        checked={formData.documents === option.value}
                        onChange={handleChange}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Moyens pédagogiques */}
            <div className="form-section">
              <h2 className="section-title">
                <span className="section-number">3</span>
                Outils & supports pédagogiques
              </h2>
              
              <div className="question-group">
                <label className="question-label">Les outils utilisés en cours théorique étaient-ils adaptés et efficaces ?</label>
                <div className="radio-group">
                  {satisfactionOptions.map(option => (
                    <label key={option.value} className="radio-option">
                      <input
                        type="radio"
                        name="outilsTheorie"
                        value={option.value}
                        checked={formData.outilsTheorie === option.value}
                        onChange={handleChange}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="question-group">
                <label className="question-label">Les outils utilisés en cours pratique répondaient-ils à vos besoins ?</label>
                <div className="radio-group">
                  {satisfactionOptions.map(option => (
                    <label key={option.value} className="radio-option">
                      <input
                        type="radio"
                        name="outilsPratique"
                        value={option.value}
                        checked={formData.outilsPratique === option.value}
                        onChange={handleChange}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Équipe pédagogique */}
            <div className="form-section">
              <h2 className="section-title">
                <span className="section-number">4</span>
                Accompagnement & enseignement
              </h2>
              
              <div className="question-group">
                <label className="question-label">La qualité de l'enseignement vous a-t-elle permis de progresser efficacement ?</label>
                <div className="radio-group">
                  {satisfactionOptions.map(option => (
                    <label key={option.value} className="radio-option">
                      <input
                        type="radio"
                        name="enseignement"
                        value={option.value}
                        checked={formData.enseignement === option.value}
                        onChange={handleChange}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="question-group">
                <label className="question-label">L'équipe pédagogique était-elle disponible et à l'écoute ?</label>
                <div className="radio-group">
                  {satisfactionOptions.map(option => (
                    <label key={option.value} className="radio-option">
                      <input
                        type="radio"
                        name="disponibilite"
                        value={option.value}
                        checked={formData.disponibilite === option.value}
                        onChange={handleChange}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Parcours de formation */}
            <div className="form-section">
              <h2 className="section-title">
                <span className="section-number">5</span>
                Déroulement de la formation
              </h2>
              
              <div className="question-group">
                <label className="question-label">Le parcours de formation a-t-il été respecté dans son intégralité ?</label>
                <div className="radio-group">
                  {satisfactionOptions.map(option => (
                    <label key={option.value} className="radio-option">
                      <input
                        type="radio"
                        name="parcoursIntegral"
                        value={option.value}
                        checked={formData.parcoursIntegral === option.value}
                        onChange={handleChange}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="question-group">
                <label className="question-label">Les cours théoriques couvraient-ils bien les différentes thématiques ?</label>
                <div className="radio-group">
                  {satisfactionOptions.map(option => (
                    <label key={option.value} className="radio-option">
                      <input
                        type="radio"
                        name="coursThematiques"
                        value={option.value}
                        checked={formData.coursThematiques === option.value}
                        onChange={handleChange}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="question-group">
                <label className="question-label">Les cours étaient-ils dispensés par un enseignant présent physiquement ?</label>
                <div className="radio-group">
                  {satisfactionOptions.map(option => (
                    <label key={option.value} className="radio-option">
                      <input
                        type="radio"
                        name="enseignantPresent"
                        value={option.value}
                        checked={formData.enseignantPresent === option.value}
                        onChange={handleChange}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="question-group">
                <label className="question-label">Le rythme de la formation était-il adapté à votre progression ?</label>
                <div className="radio-group">
                  {satisfactionOptions.map(option => (
                    <label key={option.value} className="radio-option">
                      <input
                        type="radio"
                        name="rythme"
                        value={option.value}
                        checked={formData.rythme === option.value}
                        onChange={handleChange}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="question-group">
                <label className="question-label">La durée totale de la formation vous a-t-elle semblé appropriée ?</label>
                <div className="radio-group">
                  {satisfactionOptions.map(option => (
                    <label key={option.value} className="radio-option">
                      <input
                        type="radio"
                        name="duree"
                        value={option.value}
                        checked={formData.duree === option.value}
                        onChange={handleChange}
                        required
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Note globale */}
            <div className="form-section highlight-section">
              <h2 className="section-title">
                <span className="section-icon">⭐</span>
                Bilan global
              </h2>
              <div className="question-group">
                <label className="question-label">
                  Dans l'ensemble, comment évaluez-vous votre expérience de formation ?
                </label>
                <div className="rating-slider">
                  <input
                    type="range"
                    name="noteGlobale"
                    min="0"
                    max="10"
                    value={formData.noteGlobale}
                    onChange={handleChange}
                    className="slider"
                  />
                  <div className="slider-labels">
                    <span>Pas du tout (0)</span>
                    <span className="rating-value">{formData.noteGlobale}/10</span>
                    <span>Beaucoup (10)</span>
                  </div>
                </div>
              </div>

              <div className="question-group">
                <label className="question-label">Vos remarques et suggestions</label>
                <p className="question-hint">Partagez avec nous ce qui vous a plu ou ce que nous pourrions améliorer</p>
                <textarea
                  name="commentaire"
                  value={formData.commentaire}
                  onChange={handleChange}
                  rows="5"
                  placeholder="Votre avis nous intéresse..."
                  className="form-textarea"
                />
              </div>
            </div>

            {/* Captcha */}
            <div className="form-section">
              <div className="captcha-group">
                <label htmlFor="captcha" className="captcha-label">
                  <span className="label-icon">🔒</span>
                  Sécurité
                </label>
                <p className="captcha-instruction">
                  Veuillez recopier ce code : <strong>{captchaText}</strong>
                </p>
                <input
                  type="text"
                  id="captcha"
                  name="captcha"
                  value={formData.captcha}
                  onChange={handleChange}
                  required
                  placeholder="Saisissez le code"
                  className="form-input captcha-input"
                />
              </div>
            </div>

            {/* Note anonyme */}
            <div className="form-section">
              <div className="info-box">
                <p className="info-text">
                  <strong>🔒 Votre réponse est anonyme</strong><br />
                  Aucune donnée personnelle n'est collectée. Vos réponses nous aident à améliorer nos services et votre expérience de formation.
                </p>
              </div>
            </div>

            {/* Submit */}
            {submitMessage && (
              <div className={`submit-message ${submitStatus}`}>
                {submitMessage}
              </div>
            )}

            <button 
              type="submit" 
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Envoi en cours...' : 'Transmettre mon avis'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default QuestionnaireSatisfaction;
